import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { genAttributes } from '@onward-delivery/core';
import { DELETE_SAVED_CUSTOMER } from '../../../graphql/mutations';
import { clearGeocodingLeg } from '../utils/geocoding';

/**
 * Picking a saved customer into the order, and deleting one from the picker.
 *
 * @param {object} args
 * @param {object} args.clone The draft order. Its address side decides which fields are filled.
 * @param {{ modifyOrder: (fields: object) => void }} args.modalCallbacks From `useEditModalCallbacks`.
 * @returns {{ savedCustomer: object|null, onSavedCustomerSelect: (customer: object) => void,
 * deleteSavedCustomer: Function }} `savedCustomer` is the last one picked.
 */
export const useSavedCustomer = ({ clone, modalCallbacks }) => {
    const [savedCustomer, setSavedCustomer] = useState(null);

    const [deleteSavedCustomer] = useMutation(DELETE_SAVED_CUSTOMER, {
        onError: (error) => {
            console.error(error.message);
            captureException(error);
        },
    });

    const onSavedCustomerSelect = useCallback(
        (customer) => {
            // genAttributes returns the per-side key map (dropoff_* vs pickup_*). Keep this
            // destructure in lockstep with the mappedCustomerFields block below.
            const { address, city, state, street, zip, lat, long, unit, location, location_type, geocoding } =
                genAttributes(clone);

            // Map customer fields to order fields
            const mappedCustomerFields = {
                [address]: customer?.address,
                [city]: customer?.business_city,
                [state]: customer?.business_state,
                [street]: customer?.business_address,
                [zip]: customer?.business_zip,
                [lat]: customer?.lat,
                [long]: customer?.lng,
                [unit]: customer?.business_unit,
                [location]: customer?.location_type,
                [location_type]: customer?.location_info,
                dropoff_name: customer?.contact_name,
                dropoff_phone: customer?.contact_phone,
                dropoff_email: customer?.contact_email,
                geocoding: clearGeocodingLeg(clone.geocoding, geocoding),
            };

            modalCallbacks.modifyOrder({
                ...mappedCustomerFields,
            });
            setSavedCustomer(customer);
        },
        [clone, modalCallbacks]
    );

    return { savedCustomer, onSavedCustomerSelect, deleteSavedCustomer };
};
